import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Ban,
  Camera,
  Check,
  CheckCircle2,
  ChevronRight,
  Loader2,
  RotateCcw,
  Trash2,
  TriangleAlert,
  Undo2,
  X,
} from "lucide-react";
import React, { Suspense, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import type {
  ResolvedScan,
  TransferItemCreate,
  TransferItemView,
  TransferSessionHeader,
} from "@/api/transfer_check";
import {
  type AcceptResult,
  type CompareRow,
  type ReceiptDetail,
  acceptReceipt,
  getReceipt,
  getReceiptConfig,
  isPostRefusal,
  receiptScanApi,
} from "@/api/transfer_receipts";
import { Button } from "@/components/ui/button";
import { extractErrorMessage, scanErrorTitle } from "@/lib/api-errors";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { REMAINING_EPS, boxLineBody, decideBox } from "@/lib/order-check";
import { type OrderScan, parseScan } from "@/lib/order-scan";
import { GOODS_AND_QR_FORMATS } from "@/lib/scan-formats";
import { type AcceptedBox, BoxAcceptedCard } from "@/lib/scan-ui/box-accepted";
import { CharacteristicChoice, charAnswer } from "@/lib/scan-ui/characteristic-choice";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { DUPLICATE_MARK_TEXT, sameMarkIn } from "@/lib/scan-ui/marks";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { PartiaAsk } from "@/lib/scan-ui/partia-ask";
import { QtyInput } from "@/lib/scan-ui/qty-input";
import { resolveFromIndex, toResolveRequest } from "@/lib/scan-ui/scan-resolve";
import { AccessDenied, LoadState, isForbidden, useGoBack } from "@/lib/scan-ui/screen";
import { fmtQty } from "@/lib/scan-ui/status";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";
import { type BlockedScan, BlockedScanBanner } from "@/pages/fbs/ScanBanner";
import { CompareTable, TransferFacts, commonUnit, compareKey, hasDiff, receiptTitle, withUnit } from "./shared";

/**
 * Карточка приёмки перемещения: получатель видит, что отправлено (С
 * количествами — решение владельца 28.10.2026), сканирует привезённое на
 * общем шелле `lib/scan-ui/` и жмёт «Принять». Скан — тот же путь, что у
 * отправителя (`pages/transfer-check/v2/SessionPage.tsx`), только ручки
 * получателя (`receiptScanApi`) и без правил заказа: принимается всё
 * привезённое, разница видна в сравнении, решает «Принять» на бэке.
 */

interface Draft {
  scan: OrderScan;
  resolved: ResolvedScan;
  charRef: string;
  charName: string;
  withoutChar: boolean;
  partiaRef: string;
  partiaName: string;
  partiaSource: string;
  withoutPartia: boolean;
  qty: number;
}

interface HeroItem {
  itemId: string;
  key: string;
  article: string;
  name: string;
  char: string;
  partia: string;
  coef: number;
  added: number;
  /** Штука ЧЗ или коробка: количество задано кодом, степпера нет (как в
   *  списке принятого) — бэк такую правку отбил бы. */
  fixedQty: boolean;
}

type Hero =
  | { kind: "idle" }
  | { kind: "busy"; raw: string }
  | { kind: "ok"; item: HeroItem }
  | { kind: "box"; box: AcceptedBox }
  | { kind: "error"; title: string; detail: string; raw: string; retry?: () => void };

/** Ошибка «Принять»: текст сервера; `refusal` — отказ проведения 1С (502). */
interface AcceptError {
  text: string;
  refusal: boolean;
}

export function TransferReceiptPage(): React.ReactElement {
  // Чанк камеры — при входе (см. отправителя): устаревший чанк после выката
  // перезагрузит страницу, и не посреди скана.
  usePreloadChunk(CameraScanner);
  const { sessionId } = useParams<{ sessionId: string }>();
  return (
    <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
      <ReceiptBody key={sessionId} sessionId={sessionId!} />
    </div>
  );
}

function ReceiptBody({ sessionId }: { sessionId: string }): React.ReactElement {
  const qc = useQueryClient();
  const goBack = useGoBack("/transfer-receipts");
  const detailKey = ["transfer-receipt", sessionId];
  const detailQ = useQuery({ queryKey: detailKey, queryFn: () => getReceipt(sessionId) });
  const scanApi = useMemo(() => receiptScanApi(sessionId), [sessionId]);
  const detail = detailQ.data;
  const scanning = detail?.header.status === "sent";
  const scanIndexQ = useQuery({
    queryKey: ["transfer-receipt-scan-index", sessionId],
    queryFn: () => scanApi.scanIndex(),
    enabled: scanning,
    staleTime: 3600000,
    gcTime: 3600000,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
  const refetch = () => qc.invalidateQueries({ queryKey: detailKey });

  // Способ скана — из конфига РАЗДЕЛА приёмки (как отправитель — из своего).
  const configQ = useQuery({
    queryKey: ["transfer-receipt-config"],
    queryFn: getReceiptConfig,
    staleTime: 300000,
  });
  // Пока конфиг грузится — ТСД (сканер работает всегда); отказ конфига —
  // громко плашкой с текстом сервера, а не молча «ТСД» навсегда.
  const scanMethod = configQ.data?.scan_method ?? "tsd";
  const showCamera = scanMethod === "camera" || scanMethod === "both";

  const [hero, setHero] = useState<Hero>({ kind: "idle" });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [itemsOpen, setItemsOpen] = useState(false);
  const [dialog, setDialog] = useState<null | "accept">(null);
  const [cameraScan, setCameraScan] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [result, setResult] = useState<AcceptResult | null>(null);
  const [acceptError, setAcceptError] = useState<AcceptError | null>(null);
  // Синхронный замок «Принять»: двойной тап шлёт ОДИН запрос (`isPending`
  // доезжает только с ререндером). `acceptedRef` — успех уже на экране:
  // поздний 409 гонки его не перетирает.
  const acceptingRef = useRef(false);
  const acceptedRef = useRef(false);
  // Экран итога, однажды открытый отказом проведения, не уходит обратно на
  // скан из-за следующего отказа другого рода (замок, 404) — тот виден на нём.
  const [resultShown, setResultShown] = useState(false);
  const [blocked, setBlocked] = useState<BlockedScan | null>(null);

  const busy = hero.kind === "busy";
  const scanEnabled = scanning && !draft && !itemsOpen && !dialog && !cameraScan && !manualOpen && !busy;
  const resolvingRef = useRef(false);

  function fail(title: string, text: string, raw: string, retry?: () => void) {
    setHero({ kind: "error", title, detail: text, raw, retry });
    navigator.vibrate?.([60, 60, 60]);
  }

  async function handleScan(raw: string) {
    if (!scanning || draft || itemsOpen || dialog) return;
    if (resolvingRef.current) {
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    resolvingRef.current = true;
    setBlocked(null);
    try {
      await processScan(raw);
    } finally {
      resolvingRef.current = false;
    }
  }

  async function processScan(raw: string) {
    const scan = parseScan(raw);
    const req = toResolveRequest(scan);
    if (!req) {
      fail("Не распознан код", "Это не ШК/GTIN/GS1/DataMatrix товара", raw);
      return;
    }
    // Та же штука ЧЗ второй раз — отказ сразу (бэк — 409).
    if (sameMarkIn(scan, detail?.received ?? [])) {
      fail("Уже отсканировано", DUPLICATE_MARK_TEXT, raw);
      return;
    }
    let resolved = resolveFromIndex(scan, scanIndexQ.data) ?? undefined;
    if (!resolved) {
      setHero({ kind: "busy", raw });
      try {
        resolved = await scanApi.resolve(req);
      } catch (e) {
        fail(
          scan.kind === "sscc" ? "Коробку не принять" : scanErrorTitle(e, "Ошибка 1С"),
          scan.kind === "sscc"
            ? `Не удалось получить состав коробки: ${extractErrorMessage(e, "сервер не ответил")}`
            : extractErrorMessage(e, "Не удалось связаться с 1С"),
          raw,
          () => void handleScan(raw),
        );
        return;
      }
    }
    if (!resolved.matched) {
      fail(
        resolved.scan_kind === "sscc" ? "Коробку не принять" : "Товар не найден в 1С",
        resolved.message || "Нет в 1С",
        raw,
      );
      return;
    }
    if (resolved.notice) toast.warning(resolved.notice, { duration: 10000 });
    if (resolved.box_lines?.length) {
      await acceptBox(scan, resolved);
      return;
    }
    const base = baseDraft(resolved, scan);
    if ((resolved.needs_characteristic && !base.charRef) || (resolved.needs_partia && !base.partiaRef)) {
      setDraft(base);
      return;
    }
    void addResolved(base);
  }

  /** Сборная коробка — целиком или никак. Заказа у получателя нет: любая
   *  строка «своя» (`extras` = сами строки), отказ — только повтор коробки
   *  и строки без характеристики/партии (`decideBox`). */
  async function acceptBox(scan: OrderScan, resolved: ResolvedScan) {
    const sscc = resolved.sscc;
    if (!sscc) {
      fail(
        "Коробку не принять",
        "В ответе сервера нет номера коробки (SSCC) — коробка не принята. Сообщите администратору.",
        scan.raw,
      );
      return;
    }
    const lines = resolved.box_lines ?? [];
    const verdict = decideBox([], lines, { doc: "transfer", extras: lines }, {
      sscc,
      items: detail?.received ?? [],
    });
    if (!verdict.ok) {
      fail("Коробку не принять", verdict.text, scan.raw);
      return;
    }
    try {
      await scanApi.addBox({
        raw: scan.raw,
        items: lines.map((l) => boxLineBody(l, scan, resolved.scan_kind)),
      });
    } catch (e) {
      fail("Коробку не принять", extractErrorMessage(e, "Не удалось записать коробку"), scan.raw);
      return;
    }
    navigator.vibrate?.(40);
    setHero({
      kind: "box",
      box: {
        sscc,
        lines: lines.map((l) => ({
          article: l.nomenclature_article,
          name: l.nomenclature_name,
          characteristic: l.characteristic_name,
          partia: l.partia_name,
          qty: l.qty_base,
          unit: "",
        })),
      },
    });
    // Внутри замка скана: следующая коробка решается по свежим строкам.
    await refetch();
  }

  const [confirming, setConfirming] = useState(false);
  const confirmingRef = useRef(false);
  async function confirmDraft(d: Draft, loose: boolean) {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    try {
      await addResolved(d, loose);
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
    }
  }

  async function addResolved(d: Draft, loose = false) {
    const r = d.resolved;
    const charRef = d.withoutChar ? "" : d.charRef;
    const partiaRef = d.withoutPartia ? "" : d.partiaRef;
    const body: TransferItemCreate = {
      scan_kind: r.scan_kind,
      raw: d.scan.raw,
      ean13: d.scan.ean13 ?? null,
      gtin: d.scan.gtin ?? null,
      serial: d.scan.serial ?? null,
      nomenclature_ref: r.nomenclature_ref,
      nomenclature_name: r.nomenclature_name,
      nomenclature_article: r.nomenclature_article,
      characteristic_ref: charRef,
      characteristic_name: d.withoutChar ? "" : d.charName,
      partia_ref: partiaRef,
      partia_name: d.withoutPartia ? "" : d.partiaName,
      partia_source: partiaRef ? d.partiaSource || "manual" : "none",
      unit_ref: r.unit_ref,
      unit_coefficient: loose ? 1 : r.unit_coefficient,
      qty_scanned: loose ? d.qty : r.qty_scanned,
      qty_base: d.qty,
    };
    try {
      const created = await scanApi.addItem(body);
      navigator.vibrate?.(40);
      setHero({
        kind: "ok",
        item: {
          itemId: created.id,
          key: compareKey(r.nomenclature_ref, charRef),
          article: r.nomenclature_article,
          name: r.nomenclature_name,
          char: d.withoutChar ? "" : d.charName,
          partia: d.withoutPartia ? "" : d.partiaName,
          coef: created.unit_coefficient,
          added: created.qty_base,
          fixedQty: !!(created.mark || created.sscc),
        },
      });
      setDraft(null);
      void refetch();
    } catch (e) {
      setDraft(null);
      fail("Позиция не принята", extractErrorMessage(e, "Не удалось добавить позицию"), d.scan.raw);
    }
  }

  const qtyM = useMutation({
    mutationFn: ({ id, units, coef }: { id: string; units: number; coef: number }) =>
      scanApi.setQty(id, units, coef),
    onSuccess: (item) => {
      setHero((h) =>
        h.kind === "ok" && h.item.itemId === item.id ? { ...h, item: { ...h.item, added: item.qty_base } } : h,
      );
      void refetch();
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось изменить количество")),
  });
  const deleteM = useMutation({
    mutationFn: (itemId: string) => scanApi.deleteItem(itemId),
    onSuccess: (_, itemId) => {
      toast.success("Позиция удалена");
      setHero((h) => (h.kind === "ok" && h.item.itemId === itemId ? { kind: "idle" } : h));
      void refetch();
    },
    onError: (e) => toast.error(`Удаление: ${extractErrorMessage(e, "ошибка")}`),
  });

  // «Принять»: совпало — документ создан и проведён; нет — на разбор. Отказ
  // проведения (502) — текст 1С и «Повторить» (бэк доделает без дубля).
  // Прежняя причина отказа видна, пока не придёт новый ответ (не гасим в
  // onMutate): на повторе человек должен видеть, что именно повторяет.
  const acceptM = useMutation({
    mutationFn: () => acceptReceipt(sessionId),
    onSuccess: (r) => {
      acceptedRef.current = true;
      setDialog(null);
      setAcceptError(null);
      setResult(r);
      void qc.invalidateQueries({ queryKey: ["transfer-receipts"] });
      void qc.invalidateQueries({ queryKey: ["transfer-discrepancies"] });
      if (detail?.header.kind === "free") {
        void qc.invalidateQueries({ queryKey: ["stock-transfers"] });
        void qc.invalidateQueries({ queryKey: ["stock-transfer", sessionId] });
      }
      void refetch();
    },
    onError: (e) => {
      setDialog(null);
      void refetch();
      if (acceptedRef.current) return;
      const refusal = isPostRefusal(e);
      const text = extractErrorMessage(e, refusal ? "1С не провела перемещение" : "Не удалось принять");
      // Любой отказ — в состояние: экран итога (статус мог сдвинуться после
      // перечитывания) показывает его текстом сервера. Пока сессия «едет» и
      // это не отказ проведения — человек остаётся на скане, причина в герое.
      setAcceptError({ text, refusal });
      if (refusal) setResultShown(true);
      else fail("Не принято", text, "");
    },
    onSettled: () => {
      acceptingRef.current = false;
    },
  });
  function submitAccept() {
    if (acceptingRef.current) return;
    acceptingRef.current = true;
    acceptM.mutate();
  }

  // Код пришёл, пока перехват выключен: не теряем молча — называем, почему
  // он не принят. Принятое перемещение сканировать нечем.
  function onBlocked(raw: string) {
    if (!scanning) return;
    const reason = busy
      ? "Предыдущий скан ещё обрабатывается — отсканируйте ещё раз."
      : draft
        ? "Сначала ответьте на вопрос в открытом окне, потом сканируйте."
        : cameraScan
          ? "Открыта камера — закройте её, чтобы сканировать сканером."
          : manualOpen
            ? "Открыт ручной ввод — закройте его, чтобы сканировать сканером."
            : "Сначала закройте открытое окно.";
    setBlocked({ raw, reason });
  }
  const scan = useScanCapture({
    enabled: !!scanEnabled,
    onScan: (raw) => void handleScan(raw),
    onBlocked,
  });

  if (!detail) {
    return (
      <>
        <TopBar title="Приёмка перемещения" onBack={goBack} />
        {detailQ.isError && isForbidden(detailQ.error) ? (
          <AccessDenied error={detailQ.error} />
        ) : (
          <LoadState isLoading={detailQ.isLoading} error={detailQ.error} onRetry={() => void detailQ.refetch()} />
        )}
      </>
    );
  }

  if (!scanning || result || resultShown) {
    return (
      <ResultScreen
        detail={detail}
        result={result}
        acceptError={acceptError?.text ?? null}
        retrying={acceptM.isPending}
        onRetry={submitAccept}
        onBack={goBack}
      />
    );
  }

  const rows = detail.comparison;
  const diffRows = rows.filter(hasDiff).length;
  const sentTotal = rows.reduce((s, r) => s + r.sent_qty, 0);
  const receivedTotal = rows.reduce((s, r) => s + r.received_qty, 0);
  // Итог — одной единицей, только когда она у всех строк одна (иначе число
  // без слова: сумма «шт» и «кг» слова не имеет).
  const totalUnit = commonUnit(rows.map((r) => r.base_unit_name));
  const heroRow =
    hero.kind === "ok"
      ? rows.find((r) => compareKey(r.nomenclature_ref, r.characteristic_ref) === hero.item.key)
      : undefined;

  return (
    <>
      <input
        ref={scan.scanInputRef}
        {...scan.scanInputProps}
        className="pointer-events-none fixed left-0 top-0 size-px opacity-0"
      />
      <ScanAppBar
        detail={detail}
        chip={busy ? "busy" : scanIndexQ.isError ? "warn" : scanEnabled ? "ready" : "paused"}
        onBack={goBack}
        showCamera={showCamera}
        onCamera={() => setCameraScan(true)}
        onManual={() => setManualOpen(true)}
        onReloadIndex={() => {
          void scanIndexQ.refetch();
          toast.success("Перезагружаю индекс ШК…");
        }}
      />
      <div className="border-b bg-white px-3 py-1.5">
        <TransferFacts header={detail.header} />
      </div>
      {configQ.isError && (
        <div data-testid="config-error" className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-3 py-1.5 text-[12px] text-red-900">
          <TriangleAlert className="size-4 shrink-0 text-red-700" />
          <span className="min-w-0 flex-1">
            Не загрузились настройки скана (камера/ТСД): {extractErrorMessage(configQ.error, "сервер не ответил")}.
            Работает только сканер ТСД.
          </span>
          <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => void configQ.refetch()}>
            Повторить
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between border-b bg-white px-3 py-1.5 text-[12px]">
        <span>
          Отправлено <b className="font-mono">{withUnit(sentTotal, totalUnit)}</b> · принято{" "}
          <b className="font-mono">{withUnit(receivedTotal, totalUnit)}</b>
        </span>
        {diffRows > 0 ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 font-bold text-amber-800">
            разница: {diffRows} поз.
          </span>
        ) : receivedTotal > 0 ? (
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-bold text-emerald-700">совпадает</span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
        {blocked && <BlockedScanBanner blocked={blocked} onClose={() => setBlocked(null)} />}
        <ReceiptHero
          hero={hero}
          row={heroRow}
          onUndo={(id) => deleteM.mutate(id)}
          onSetQty={(id, units, coef) => qtyM.mutate({ id, units, coef })}
          onDismiss={() => setHero({ kind: "idle" })}
        />
        <CompareTable rows={rows} highlightKey={hero.kind === "ok" ? hero.item.key : undefined} />
      </div>

      <button
        type="button"
        onClick={() => setItemsOpen(true)}
        className="flex items-center justify-between border-t bg-white px-3 py-2 text-left"
      >
        <span className="text-[13px] font-bold">Принято строк: {detail.received.length}</span>
        <span className="flex items-center gap-2 text-[11px] text-slate-500">
          править / удалить
          <ChevronRight className="size-4 text-slate-300" />
        </span>
      </button>
      <div className="border-t bg-white p-2">
        <button
          type="button"
          disabled={detail.received.length === 0 || acceptM.isPending}
          onClick={() => setDialog("accept")}
          className={cn(
            "flex h-14 w-full items-center justify-center gap-2 rounded-[10px] border text-[14px] font-bold",
            detail.received.length === 0
              ? "border-slate-200 bg-slate-100 text-slate-500"
              : detail.matches
                ? "border-emerald-600 bg-emerald-600 text-white"
                : "border-amber-500 bg-amber-500 text-white",
          )}
        >
          {acceptM.isPending && <Loader2 className="size-4 animate-spin" />}
          {detail.received.length === 0
            ? "Отсканируйте привезённое"
            : acceptM.isPending
              ? "Принимаем…"
              : detail.matches
                ? "✓ Принять — совпадает"
                : "Принять — есть расхождение ›"}
        </button>
      </div>

      {showCamera && !cameraScan && (
        <button
          type="button"
          onClick={() => setCameraScan(true)}
          className="fixed bottom-24 right-4 z-30 flex h-14 items-center gap-2 rounded-full bg-sky-700 px-5 text-[14px] font-bold text-white shadow-lg"
        >
          <Camera className="size-5" /> Сканировать камерой
        </button>
      )}

      {cameraScan && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              formats={GOODS_AND_QR_FORMATS}
              onDetect={(raw) => {
                setCameraScan(false);
                void handleScan(raw);
              }}
              onClose={() => setCameraScan(false)}
            />
          </div>
        </Suspense>
      )}

      {draft && (
        <ClarifySheet
          draft={draft}
          setDraft={setDraft}
          lookupPartia={(nom, name) => scanApi.partia(nom, name)}
          sentRows={rows}
          busy={confirming}
          onCancel={() => {
            setDraft(null);
            setHero({ kind: "idle" });
          }}
          onConfirm={(loose) => void confirmDraft(draft, loose)}
        />
      )}

      {itemsOpen && (
        <ReceivedSheet
          items={detail.received}
          busy={qtyM.isPending || deleteM.isPending}
          onClose={() => setItemsOpen(false)}
          onDelete={(id) => deleteM.mutate(id)}
          onSetQty={(id, units, coef) => qtyM.mutate({ id, units, coef })}
        />
      )}

      {manualOpen && (
        <ManualCodeSheet
          onSubmit={(code) => {
            setManualOpen(false);
            void handleScan(code);
          }}
          onClose={() => setManualOpen(false)}
        />
      )}

      {dialog === "accept" && (
        <Dialog
          title={detail.matches ? "Принять и провести?" : "Принятое не совпадает с отправленным"}
          body={
            detail.matches ? (
              // Без заказа подгонять нечего — о заказе ни слова.
              detail.header.kind === "free"
                ? "Совпадает с отправленным. Портал создаст и проведёт «Перемещение запасов» в 1С."
                : "Совпадает с отправленным. Портал подгонит заказ, создаст и проведёт «Перемещение запасов» в 1С."
            ) : (
              <>
                Позиций с разницей: <b>{diffRows}</b>. Перемещение уйдёт на разбор, в 1С ничего не
                запишется, пока расхождение не решат.
              </>
            )
          }
          confirmLabel={detail.matches ? "Принять" : "Передать на разбор"}
          confirmBusy={acceptM.isPending}
          danger={!detail.matches}
          onConfirm={submitAccept}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}

function baseDraft(resolved: ResolvedScan, scan: OrderScan): Draft {
  return {
    scan,
    resolved,
    charRef: resolved.characteristic_ref,
    charName: resolved.characteristic_name,
    withoutChar: false,
    partiaRef: resolved.partia_ref,
    partiaName: resolved.partia_name,
    partiaSource: resolved.partia_source,
    withoutPartia: false,
    qty: resolved.qty_base || 1,
  };
}

// ─────────────────────────── шапки ────────────────────────────────────────

function TopBar({ title, onBack }: { title: string; onBack: () => void }): React.ReactElement {
  return (
    <div className="flex h-[46px] items-center gap-1 bg-slate-900 px-1 text-white">
      <button type="button" onClick={onBack} className="flex size-9 items-center justify-center" aria-label="Назад">
        <ArrowLeft className="size-5" />
      </button>
      <div className="truncate text-[14px] font-bold">{title}</div>
    </div>
  );
}

function ScanAppBar({
  detail,
  chip,
  onBack,
  showCamera,
  onCamera,
  onManual,
  onReloadIndex,
}: {
  detail: ReceiptDetail;
  chip: "ready" | "busy" | "paused" | "warn";
  onBack: () => void;
  /** `scan_method` camera/both из конфига приёмки. */
  showCamera: boolean;
  onCamera: () => void;
  onManual: () => void;
  onReloadIndex: () => void;
}): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const h = detail.header;
  const chipMap = {
    ready: { dot: "bg-emerald-400", bg: "bg-emerald-500/20", t: "Сканер готов" },
    busy: { dot: "bg-sky-400", bg: "bg-sky-500/20", t: "Ищем в 1С…" },
    paused: { dot: "bg-slate-400", bg: "bg-white/10", t: "Скан на паузе" },
    warn: { dot: "bg-amber-400", bg: "bg-amber-500/20", t: "Индекс ШК не загружен" },
  }[chip];
  const item = "block w-full px-3 py-2.5 text-left text-[13px] hover:bg-slate-100";
  return (
    <div className="relative bg-slate-900 text-white">
      <div className="flex h-[46px] items-center gap-1 px-1">
        <button type="button" onClick={onBack} className="flex size-9 items-center justify-center" aria-label="Назад">
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1 leading-tight">
          {/* Номер — целиком: на 360 px рядом с чипом сканера обрезалось
              «Приёмка · Перемещение …», и номер пропадал. Сжимается приставка. */}
          <div className="flex min-w-0 text-[13px] font-bold" data-testid="receipt-bar-title">
            <span className="truncate">Приёмка ·&nbsp;</span>
            <span className="shrink-0">{receiptTitle(h)}</span>
          </div>
          <div className="truncate text-[10px] text-slate-400">
            {h.from_name} → {h.to_name}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMenuOpen((s) => !s)}
          className={cn("mr-1 flex h-[30px] items-center gap-1.5 rounded-full px-2.5", chipMap.bg)}
        >
          <span className={cn("size-2 rounded-full", chipMap.dot)} />
          <span className="text-[11px] font-semibold">{chipMap.t}</span>
        </button>
      </div>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-2 top-[44px] z-40 w-56 overflow-hidden rounded-lg bg-white text-slate-800 shadow-xl">
            {(
              [
                ...(showCamera ? ([["Камера-сканер", onCamera]] as const) : []),
                ["Ввести код вручную", onManual],
                ["Повторить загрузку индекса ШК", onReloadIndex],
              ] as const
            ).map(([label, act]) => (
              <button
                key={label}
                type="button"
                className={item}
                onClick={() => {
                  setMenuOpen(false);
                  act();
                }}
              >
                {label === "Камера-сканер" && <Camera className="mr-1.5 inline size-4" />}
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── герой ────────────────────────────────────────

function ReceiptHero({
  hero,
  row,
  onUndo,
  onSetQty,
  onDismiss,
}: {
  hero: Hero;
  /** Строка сравнения позиции последнего скана — «отправлено · принято». */
  row?: CompareRow;
  onUndo: (itemId: string) => void;
  onSetQty: (itemId: string, units: number, coef: number) => void;
  onDismiss: () => void;
}): React.ReactElement | null {
  if (hero.kind === "idle") {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white px-3 py-3 text-center">
        <div className="text-[15px] font-bold text-slate-700">Сканируйте привезённое</div>
        <div className="text-[11px] text-slate-400">отправленное — в таблице ниже</div>
      </div>
    );
  }
  if (hero.kind === "busy") {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sky-700">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-[14px] font-bold">Ищем в 1С…</span>
        <span className="truncate font-mono text-[11px] text-slate-500">{hero.raw.slice(0, 30)}</span>
      </div>
    );
  }
  if (hero.kind === "error") {
    return (
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3" data-testid="hero-error">
        <div className="text-[17px] font-bold text-red-700">{hero.title}</div>
        <div className="mt-1 whitespace-pre-line text-[12px] text-red-900">{hero.detail}</div>
        {hero.raw && (
          <div className="mt-2 break-all rounded bg-white p-2 font-mono text-[11px] text-slate-500">{hero.raw}</div>
        )}
        <div className="mt-2 flex gap-2">
          {hero.retry && (
            <Button className="h-11 flex-1 bg-red-700 hover:bg-red-800" onClick={hero.retry}>
              <RotateCcw className="size-4" /> Повторить
            </Button>
          )}
          <Button variant="outline" className="h-11 flex-1" onClick={onDismiss}>
            Продолжить скан
          </Button>
        </div>
      </div>
    );
  }
  if (hero.kind === "box") return <BoxAcceptedCard box={hero.box} />;
  const it = hero.item;
  const units = it.coef > 1 ? it.added / it.coef : it.added;
  return (
    <div key={it.itemId} data-testid="hero-ok" className="oc-flash rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-2.5">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[19px] font-bold">{it.article}</span>
        {it.coef > 1 && (
          <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
            короб ×{fmtQty(it.coef)}
          </span>
        )}
        {/* До перечитывания detail строки сравнения ещё нет — число остаётся
            без единицы: слово наугад не подставляем. */}
        <span className="ml-auto text-[15px] font-bold text-emerald-700">
          +{withUnit(it.added, row?.base_unit_name)}
        </span>
        <Check className="size-5 text-emerald-600" />
      </div>
      <div className="line-clamp-1 text-[11px] text-slate-600">
        {it.name}
        {it.char ? ` · ${it.char}` : ""}
        {it.partia ? ` · парт. ${it.partia}` : ""}
      </div>
      {row && (
        <div
          className={cn(
            "mt-1.5 rounded-md border px-2 py-1 text-[12px] font-semibold",
            hasDiff(row) ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-200 bg-white text-emerald-700",
          )}
        >
          отправлено {withUnit(row.sent_qty, row.base_unit_name)} · принято{" "}
          {withUnit(row.received_qty, row.base_unit_name)}
          {Math.abs(row.diff) > REMAINING_EPS
            ? ` · разница ${row.diff > 0 ? "+" : "−"}${withUnit(Math.abs(row.diff), row.base_unit_name)}`
            : row.partia_mismatch
              ? ""
              : " · ✓"}
          {row.partia_mismatch && row.partia_note && <div className="font-normal">{row.partia_note}</div>}
        </div>
      )}
      {!it.fixedQty && (
      <div className="mt-1.5 flex items-center justify-between gap-2" data-testid="hero-stepper">
        {/* При coef = 1 степпер правит БАЗОВЫЕ единицы: подписываем их
            настоящим именем из сравнения, а не «Штук:» (соврало бы для
            «упак»/«кг»); имени нет — «Количество:», слово не выдумываем. */}
        <span className="text-[12px] font-semibold text-slate-500">
          {it.coef > 1 ? "Коробов:" : row?.base_unit_name ? `${row.base_unit_name}:` : "Количество:"}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="size-9 rounded-lg border bg-white text-[20px]"
            onClick={() => onSetQty(it.itemId, Math.max(1, units - 1), it.coef)}
            aria-label="Меньше"
          >
            −
          </button>
          <QtyInput value={units} onChange={(v) => v >= 1 && onSetQty(it.itemId, v, it.coef)} />
          <button
            type="button"
            className="size-9 rounded-lg border bg-white text-[20px]"
            onClick={() => onSetQty(it.itemId, units + 1, it.coef)}
            aria-label="Больше"
          >
            +
          </button>
        </div>
      </div>
      )}
      <Button variant="outline" className="mt-1.5 h-10 w-full" onClick={() => onUndo(it.itemId)}>
        <RotateCcw className="size-4" /> Отменить последний скан
      </Button>
    </div>
  );
}

// ─────────────────────────── уточнение скана ──────────────────────────────

function ClarifySheet({
  draft,
  setDraft,
  lookupPartia,
  sentRows,
  busy,
  onCancel,
  onConfirm,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  lookupPartia: (nomenclatureRef: string, name: string) => Promise<{ ref: string; name: string }>;
  sentRows: CompareRow[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: (loose: boolean) => void;
}): React.ReactElement {
  const r = draft.resolved;
  const [partiaInput, setPartiaInput] = useState(draft.partiaName || draft.scan.batch || "");
  const partiaM = useMutation({
    mutationFn: (name: string) => lookupPartia(r.nomenclature_ref, name),
    onSuccess: (p) =>
      setDraft({ ...draft, partiaRef: p.ref, partiaName: p.name, partiaSource: "manual", withoutPartia: false }),
    onError: (e) => {
      const st = (e as { response?: { status?: number } })?.response?.status;
      toast.error(st === 404 ? "Партия не найдена в 1С" : extractErrorMessage(e, "Ошибка проверки партии"));
    },
  });
  const needChar = r.needs_characteristic && !draft.charRef && !draft.withoutChar;
  const partiaReady = !r.needs_partia || draft.withoutPartia || !!draft.partiaRef;
  const coef = r.unit_coefficient;
  const loose = coef > 1 && draft.qty % coef !== 0;
  const canAdd = !needChar && partiaReady && draft.qty > 0;
  const reason = needChar
    ? "Выберите характеристику"
    : !partiaReady
      ? "Укажите партию или «без партии»"
      : draft.qty <= 0
        ? "Укажите количество"
        : "";
  // Что отправлено по этому товару — подсказка выбору характеристики/партии.
  const sentForNom = sentRows.filter((x) => x.nomenclature_ref === r.nomenclature_ref && x.sent_qty > 0);
  // Количество в черновике — БАЗОВЫЕ единицы: подписываем базовой единицей
  // номенклатуры (у строк одной номенклатуры она одна); пусто — слово не выдумываем.
  const unit = sentForNom[0]?.base_unit_name ?? "";

  return (
    <BottomSheet onClose={onCancel} panelClassName="max-h-[85%] overflow-y-auto">
      <div className="flex items-start gap-2 p-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[18px] font-bold">{r.nomenclature_article}</span>
            {coef > 1 && (
              <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
                короб ×{fmtQty(coef)}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[16px] font-bold">
            {needChar ? "Выберите характеристику" : !partiaReady ? "Укажите партию" : "Уточните скан"}
          </div>
          <div className="line-clamp-1 text-[11px] text-slate-500">{r.nomenclature_name}</div>
        </div>
        <button type="button" onClick={onCancel} className="flex size-8 items-center justify-center" aria-label="Закрыть">
          <X className="size-4" />
        </button>
      </div>
      <div className="space-y-3 px-3 pb-3">
        {sentForNom.length > 0 ? (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-2 text-[12px] text-sky-800">
            Отправлено по товару:
            {sentForNom.map((x) => (
              <div key={compareKey(x.nomenclature_ref, x.characteristic_ref)}>
                {x.characteristic_name || "без характ."}
                {x.partia_name ? ` · парт. ${x.partia_name}` : ""} — <b>{withUnit(x.sent_qty, x.base_unit_name)}</b>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-[12px] font-semibold text-amber-800">
            Этот товар не отправляли — будет расхождение.
          </div>
        )}
        <CharacteristicChoice
          options={r.available_characteristics}
          answer={{ charRef: draft.charRef, charName: draft.charName, withoutChar: draft.withoutChar }}
          onPick={(c) => setDraft({ ...draft, ...charAnswer(c) })}
          required={needChar}
        />
        {r.needs_partia && (
          <PartiaAsk
            value={partiaInput}
            onChange={setPartiaInput}
            hasPicked={!!draft.partiaRef}
            picked={draft.partiaName}
            onReset={() => setDraft({ ...draft, partiaRef: "", partiaName: "", partiaSource: "" })}
            onLookup={(name) => partiaM.mutate(name)}
            busy={partiaM.isPending}
            withoutPartia={draft.withoutPartia}
            onWithoutPartia={(v) => setDraft({ ...draft, withoutPartia: v })}
          />
        )}
        <div className="rounded-lg border p-2">
          <div className="mb-1.5 text-[12px] font-semibold text-slate-500">Количество:</div>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              className="size-12 rounded-lg border text-[22px]"
              onClick={() => setDraft({ ...draft, qty: Math.max(0, draft.qty - 1) })}
            >
              −
            </button>
            <QtyInput value={draft.qty} onChange={(v) => setDraft({ ...draft, qty: v })} />
            <button
              type="button"
              className="size-12 rounded-lg border text-[22px]"
              onClick={() => setDraft({ ...draft, qty: draft.qty + 1 })}
            >
              +
            </button>
          </div>
          <div className="mt-1 text-center text-[11px] text-slate-500">
            {coef > 1
              ? loose
                ? "частичный короб — россыпь"
                : `= ${fmtQty(draft.qty / coef)} кор.`
              : unit || "количество"}
          </div>
        </div>
      </div>
      <div className="flex gap-2 border-t p-3">
        <Button variant="outline" className="h-[52px] flex-[0.4]" onClick={onCancel}>
          Отмена
        </Button>
        <Button
          className="h-[52px] flex-1 bg-emerald-600 hover:bg-emerald-700"
          disabled={!canAdd || busy}
          onClick={() => onConfirm(loose)}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}{" "}
          {canAdd ? `Добавить ${withUnit(draft.qty, unit)}` : reason}
        </Button>
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── принятые строки ──────────────────────────────

function ReceivedSheet({
  items,
  busy,
  onClose,
  onDelete,
  onSetQty,
}: {
  items: TransferItemView[];
  busy: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
  onSetQty: (id: string, units: number, coef: number) => void;
}): React.ReactElement {
  const rows = [...items].reverse();
  return (
    <BottomSheet onClose={onClose} panelClassName="flex max-h-[90%] flex-col">
      <div className="flex items-center justify-between border-b p-3">
        <div className="text-[15px] font-bold">Принято ({rows.length})</div>
        <button type="button" onClick={onClose} className="flex size-8 items-center justify-center" aria-label="Закрыть">
          <X className="size-4" />
        </button>
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
        {rows.length === 0 && (
          <div className="py-6 text-center text-[13px] text-slate-400">Пока ничего не отсканировано</div>
        )}
        {rows.map((it) => {
          const coef = it.unit_coefficient;
          const units = it.qty_base / coef;
          return (
            <div key={it.id} data-testid="received-row" className="rounded-lg border bg-white p-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="font-mono text-[13px] font-bold">{it.nomenclature_article}</span>
                    {it.characteristic_name && (
                      <span className="rounded border bg-slate-50 px-1 text-[10px]">{it.characteristic_name}</span>
                    )}
                    {it.partia_name && (
                      <span className="rounded border bg-slate-50 px-1 font-mono text-[10px]">парт. {it.partia_name}</span>
                    )}
                    {coef > 1 && (
                      <span className="rounded border border-sky-200 bg-sky-100 px-1 text-[10px] font-bold text-sky-800">
                        короб ×{fmtQty(coef)}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-slate-600">{it.nomenclature_name}</div>
                </div>
                <span className="shrink-0 font-mono text-[15px] font-bold tabular-nums">
                  {withUnit(it.qty_base, it.base_unit_name)}
                </span>
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-2">
                {!it.mark && !it.sscc ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      className="size-9 rounded-lg border text-[20px]"
                      onClick={() => onSetQty(it.id, Math.max(1, units - 1), coef)}
                      aria-label="Меньше"
                    >
                      −
                    </button>
                    <QtyInput value={units} onChange={(v) => v >= 1 && onSetQty(it.id, v, coef)} />
                    <button
                      type="button"
                      disabled={busy}
                      className="size-9 rounded-lg border text-[20px]"
                      onClick={() => onSetQty(it.id, units + 1, coef)}
                      aria-label="Больше"
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <span className="text-[11px] text-slate-400">
                    {it.mark ? "штука ЧЗ" : it.sscc ? `коробка ${it.sscc}` : ""}
                  </span>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onDelete(it.id)}
                  className="flex size-9 items-center justify-center rounded-md border border-red-200 text-red-600"
                  aria-label="Удалить"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
}

function ManualCodeSheet({
  onSubmit,
  onClose,
}: {
  onSubmit: (code: string) => void;
  onClose: () => void;
}): React.ReactElement {
  const [v, setV] = useState("");
  return (
    <BottomSheet onClose={onClose} panelClassName="p-3">
      <div className="mb-2 text-[15px] font-bold">Ввести код вручную</div>
      <div className="flex gap-2">
        <input
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="ШК / GTIN / GS1"
          className="h-[50px] min-w-0 flex-1 rounded-lg border px-3 font-mono text-[14px]"
        />
        <Button className="h-[50px] bg-sky-700" disabled={!v.trim()} onClick={() => onSubmit(v.trim())}>
          ОК
        </Button>
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── итог ─────────────────────────────────────────

function ResultScreen({
  detail,
  result,
  acceptError,
  retrying,
  onRetry,
  onBack,
}: {
  detail: ReceiptDetail;
  result: AcceptResult | null;
  /** Текст последнего отказа «Принять» (любого: 502, 409, 404, 400, 500) — как есть. */
  acceptError: string | null;
  retrying: boolean;
  onRetry: () => void;
  onBack: () => void;
}): React.ReactElement {
  // Перечитанная шапка может ещё не доехать после «Принять» — пока она
  // «sent», правду говорит ответ «Принять».
  const h = detail.header.status === "sent" && result ? result.header : detail.header;
  const view = resultView(h, result);
  const comparison = result?.comparison ?? detail.comparison;
  const diffRows = comparison.filter(hasDiff);
  const docLabel = view.docNumber ? `Перемещение № ${view.docNumber}` : "Перемещение";

  let panel: React.ReactElement;
  let errorInPanel = false;
  switch (view.kind) {
    case "discrepancy":
      panel = (
        <div data-testid="receipt-result" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-amber-900">
          <div className="flex items-center gap-2 text-[17px] font-bold">
            <TriangleAlert className="size-5" /> Расхождение, передано на разбор
          </div>
          <div className="mt-1 text-[13px]">
            В 1С ничего не записано. Позиций с разницей: {diffRows.length}. Решение — в «Расхождениях
            перемещений».
          </div>
        </div>
      );
      break;
    case "by_receipt_pending":
      // Решение «по принятому» принял разбор — и доводит его тоже он: у
      // получателя «Принять» здесь 409, «Повторить» был бы ложной кнопкой.
      panel = (
        <div data-testid="receipt-result" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-amber-900">
          <div className="flex items-center gap-2 text-[17px] font-bold">
            <TriangleAlert className="size-5" /> {docLabel} пока не проведено
          </div>
          <div className="mt-1 text-[13px]">
            Документ проводит разбор расхождений: решено провести по принятому, проведение в 1С не
            доведено. От вас ничего не нужно.
          </div>
        </div>
      );
      break;
    case "posted":
      panel = (
        <div data-testid="receipt-result" className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 text-emerald-800">
          <div className="flex items-center gap-2 text-[17px] font-bold">
            <CheckCircle2 className="size-5" /> {docLabel} проведено
          </div>
          <div className="mt-1 text-[13px]">
            {h.resolution === "by_receipt"
              ? "Проведено по принятому (после разбора расхождения)."
              : "Принятое совпало с отправленным."}
          </div>
        </div>
      );
      break;
    case "retry":
      // Документ создан, а проведение 1С отклонила (502) — или решение
      // сохранено, а шаги 1С не доведены: текст отказа и «Повторить» (бэк
      // доделает без второго документа).
      errorInPanel = true;
      panel = (
        <div data-testid="receipt-result" className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-red-900">
          <div className="flex items-center gap-2 text-[17px] font-bold text-red-700">
            <TriangleAlert className="size-5" /> {docLabel} не проведено
          </div>
          <div className="mt-1 whitespace-pre-line text-[13px]">
            {acceptError ?? "1С не провела документ. Нажмите «Повторить» — портал доделает проведение."}
          </div>
          <Button className="mt-2 h-12 w-full bg-red-700 hover:bg-red-800" disabled={retrying} onClick={onRetry}>
            {retrying ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />} Повторить
          </Button>
        </div>
      );
      break;
    case "cancelled":
      panel = (
        <StatusPanel icon={<Ban className="size-5" />} title="Перемещение отменено" text="В 1С ничего не записано." />
      );
      break;
    case "reopened":
      panel = (
        <StatusPanel
          icon={<Undo2 className="size-5" />}
          title="Перемещение возвращено в работу отправителю"
          text="Принимать пока нечего: отправитель снова собирает его. Когда отправит — оно появится во входящих."
        />
      );
      break;
    default:
      panel = (
        <StatusPanel
          icon={<TriangleAlert className="size-5" />}
          title={`Статус перемещения: ${h.status}`}
          text="Приёмка по нему сейчас не идёт."
        />
      );
  }

  return (
    <>
      <TopBar title={`Приёмка · ${receiptTitle(h)}`} onBack={onBack} />
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        <div className="rounded-lg border bg-white p-3">
          <TransferFacts header={h} />
        </div>

        {panel}

        {acceptError && !errorInPanel && (
          <div data-testid="accept-error" className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-red-900">
            <div className="text-[13px] font-bold text-red-700">«Принять» не выполнено</div>
            <div className="mt-1 whitespace-pre-line text-[13px]">{acceptError}</div>
          </div>
        )}

        <CompareTable rows={comparison} />

        <Button variant="outline" className="h-12 w-full" onClick={onBack}>
          К входящим
        </Button>
      </div>
    </>
  );
}

type ResultKind =
  | "discrepancy"
  | "by_receipt_pending"
  | "posted"
  | "retry"
  | "cancelled"
  | "reopened"
  | "other";

/** Что показать на экране итога — по статусу, решению и доведённости до 1С
 *  (`transfer_doc_posted`, `pending_resolution`), а не по одному статусу. */
export function resultView(
  h: TransferSessionHeader,
  result: AcceptResult | null,
): { kind: ResultKind; docNumber: string | null } {
  const docNumber = result?.transfer_doc_number || h.transfer_doc_number;
  const posted = ((result?.posted ?? false) || h.transfer_doc_posted) && !h.pending_resolution;
  const kind = ((): ResultKind => {
    if (h.resolution === "by_receipt") {
      if (h.status === "completed" && posted) return "posted";
      return "by_receipt_pending";
    }
    if (h.status === "discrepancy" || result?.outcome === "discrepancy") return "discrepancy";
    if (h.status === "completed") return posted ? "posted" : "retry";
    // «Едет», а сюда попали: «Принять» отказал проведением (502) или решение
    // «совпало» сохранено без доведения — повтор доделывает.
    if (h.status === "sent") return "retry";
    if (h.status === "cancelled") return "cancelled";
    if (h.status === "open") return "reopened";
    return "other";
  })();
  return { kind, docNumber };
}

function StatusPanel({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}): React.ReactElement {
  return (
    <div data-testid="receipt-result" className="rounded-lg border bg-slate-100 p-3 text-slate-700">
      <div className="flex items-center gap-2 text-[17px] font-bold">
        {icon} {title}
      </div>
      <div className="mt-1 text-[13px]">{text}</div>
    </div>
  );
}
